import React, { useRef, useState } from 'react';
import classnames from 'classnames';

import { FormattedMessage } from 'react-intl';
import { MeetingAttendeeIssue, MeetingAttendeeJoinMethod, MeetingAttendeeStatus } from '../../../../../../../api/attendees.dao';
import { Popover } from '../../../../../../global/popover/popover';
import OEMultiSelect from '../../../../../../global/oe-multi-select/oe-multi-select';
import { EditableArea } from '../../../../../../global/editable-area/editable-area';
import { MEDIUM_PLAIN_TEXT_LENGTH } from '../../../../../../../constants/common.constants';
import ChevronSVG from '../../../../../../../images/svg/icons8-expand-arrow';
import Text from '../../../../../../global/text/text';

import './status-button.scss';


interface IProps {
    status: MeetingAttendeeStatus;
    isAttendeeJoined: boolean;
    issues: MeetingAttendeeIssue[];
    onChangeAttendeeIssue: (newIssues: MeetingAttendeeIssue[]) => void;
    meetingAttendeeNote: string;
    onChangeMeetingNote: (newNote: string) => void;
    onNoShowClick: (value: MeetingAttendeeStatus) => void;
    withTableView: boolean;
    joinMethod?: MeetingAttendeeJoinMethod;
    isMeetingUnderway: boolean;
}

const StatusButton = (props: IProps) => {
    const {
        status,
        isAttendeeJoined,
        issues,
        onChangeAttendeeIssue,
        meetingAttendeeNote,
        onChangeMeetingNote,
        onNoShowClick,
        withTableView = false,
        joinMethod,
        isMeetingUnderway
    } = props;

    const buttonRef = useRef(null);
    const [isModalOpen, setModalOpen] = useState(false);
    const isHiddenOptions = withTableView && joinMethod === MeetingAttendeeJoinMethod.IN_PERSON;
    const shouldHighlight = isMeetingUnderway && status === MeetingAttendeeStatus.CONFIRMED;

    const statusOptions = [
        { label: 'No Show', id: 'oe.common.noShow', value: MeetingAttendeeStatus.NOSHOW },
        { label: 'Cancelled', id: 'oe.common.cancelled', value: MeetingAttendeeStatus.CANCELLED }
    ];

    const issueOptions = [
        { label: 'Bad Audio', id: 'oe.common.badAudio', value: MeetingAttendeeIssue.BAD_AUDIO },
        { label: 'Bad Video', id: 'oe.common.badVideo', value: MeetingAttendeeIssue.BAD_VIDEO },
        { label: 'Connection Dropped', id: 'oe.common.connectionDropped', value: MeetingAttendeeIssue.CONNECTION_DROPPED },
    ];

    const attendeeNoteValidator = (content: string) => {
        if (content.length > MEDIUM_PLAIN_TEXT_LENGTH) {
            return <>
                <FormattedMessage
                    id='oe.attendee.attendeeNoteTooLong'
                    defaultMessage='The attendee note is too long.'
                    description='Error label text'
                />
                <FormattedMessage
                    id='oe.attendee.MaxLengthIs'
                    defaultMessage='Max length is {length} characters'
                    description='Error label text'
                    values={{
                        length: MEDIUM_PLAIN_TEXT_LENGTH
                    }}
                />
            </>;
        }
        return null;
    };

    const getStatusButtonText = () => {
        if (isAttendeeJoined) {
            if (issues.length) {
                return issueOptions.filter(el => issues.includes(el.value)).map(el => el.label).join(', ');
            }
            return 'Report issues';
        }

        if (status === MeetingAttendeeStatus.NOSHOW) return 'No Show';
        if (status === MeetingAttendeeStatus.CANCELLED) return 'Cancelled';
        return 'Report status';
    };

    return (
        <div
            className={classnames('status-button', {
                'status-button--active': isModalOpen,
                'status-button--no-show': status === MeetingAttendeeStatus.NOSHOW,
                'status-button--cancelled': status === MeetingAttendeeStatus.CANCELLED,
                'status-button--no-active': isHiddenOptions,
            })}
            ref={buttonRef}
        >
            {
                !isHiddenOptions &&
                <>
                    <div className={classnames('status-button__wrapper', {'status-button__wrapper--highlighted': shouldHighlight})}
                         onClick={() => setModalOpen(prevState => !prevState)}>
                        <Text
                            className={classnames('status-button__label', {
                                'status-button__label--with-value': issues.length
                            })}
                            message={getStatusButtonText()}
                            withEllipsis
                        />
                        <div className='status-button__chevron' >
                            <ChevronSVG />
                        </div>
                    </div>
                    <Popover
                        padding={0}
                        isOpen={isModalOpen}
                        buttonRef={buttonRef}
                        customWidth={withTableView ? buttonRef.current?.getBoundingClientRect().width : 147}
                        customLeft={withTableView ? undefined : -50}
                        onClickOutside={() => setModalOpen(false)}
                    >
                        {joinMethod !== MeetingAttendeeJoinMethod.IN_PERSON && isAttendeeJoined &&
                            <OEMultiSelect<MeetingAttendeeIssue>
                                options={issueOptions}
                                values={issues}
                                onChange={onChangeAttendeeIssue}
                            />
                        }
                        {!isAttendeeJoined &&
                            <div className='status-button__options'>
                                {statusOptions.map(el =>
                                    <div
                                        className={classnames('status-button__option', {
                                            'status-button__option--selected': status === el.value
                                        })}
                                        key={el.label}
                                        onClick={() => onNoShowClick(el.value)}
                                    >
                                        {
                                            el.id ? <FormattedMessage
                                                id={el.id}
                                                defaultMessage={el.label}
                                                description='Attendee status button text'
                                            /> : el.label
                                        }
                                    </div>
                                )}
                            </div>
                        }
                        {!withTableView && <div className='status-button__content'>
                            <div className='status-button__content-label'>Attendee Note</div>
                            <EditableArea
                                content={meetingAttendeeNote || ''}
                                onSave={onChangeMeetingNote}
                                validator={attendeeNoteValidator}
                            />
                        </div>}
                    </Popover>
                </>
            }
        </div>
    );
};

export default StatusButton;
